import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  ChevronRight,
  Clock,
  ListTree,
  User,
} from "lucide-react";
import { ArticleCard } from "./BlogList";
import Seo, { buildBreadcrumb, buildArticleSchema } from "../components/Seo";
import PicImg from "../components/PicImg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------- Helpers ---------- */
const slugify = (str) =>
  String(str)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

/* Extract H2 headings from markdown for the Table of Contents */
function extractToc(content) {
  if (!content) return [];
  const lines = content.split("\n");
  return lines
    .filter((l) => l.startsWith("## "))
    .map((l) => l.replace(/^##\s+/, "").trim())
    .filter((t) => t.length > 0 && t.toLowerCase() !== "table of contents")
    .map((t) => ({ title: t, id: slugify(t) }));
}

/* Strip the inline "## Table of Contents" block from the markdown body so we
   don't render it twice (the premium TOC card at the top already covers it). */
function stripInlineToc(content) {
  if (!content) return "";
  // Remove "## Table of Contents" through to the next H2 (exclusive).
  return content.replace(
    /^##\s+Table of Contents[\s\S]*?(?=^##\s+)/im,
    "",
  );
}

/* ---------- Markdown component overrides ---------- */
const mdComponents = {
  h2: ({ children, ...props }) => {
    const text = String(children);
    return (
      <h2
        id={slugify(text)}
        className="font-display uppercase text-liftile-cream tracking-tight text-2xl sm:text-3xl md:text-[2rem] leading-[1.15] mt-14 mb-6 scroll-mt-28"
        {...props}
      >
        {children}
      </h2>
    );
  },
  h3: ({ children, ...props }) => (
    <h3
      className="font-heading uppercase tracking-wider text-liftile-cream text-base md:text-lg font-semibold mt-10 mb-4"
      {...props}
    >
      {children}
    </h3>
  ),
  p: ({ children, ...props }) => (
    <p
      className="font-body text-liftile-cream/80 text-base md:text-[17px] leading-[1.85] my-5"
      {...props}
    >
      {children}
    </p>
  ),
  a: ({ children, ...props }) => (
    <a
      className="text-liftile-orange underline underline-offset-2 hover:opacity-80"
      {...props}
    >
      {children}
    </a>
  ),
  strong: ({ children, ...props }) => (
    <strong className="text-liftile-cream font-semibold" {...props}>
      {children}
    </strong>
  ),
  em: ({ children, ...props }) => (
    <em className="text-liftile-cream/90 italic" {...props}>
      {children}
    </em>
  ),
  ul: ({ children, ...props }) => (
    <ul className="list-none space-y-3 my-6 pl-0" {...props}>
      {children}
    </ul>
  ),
  ol: ({ children, ...props }) => (
    <ol className="list-decimal space-y-3 my-6 pl-6 marker:text-liftile-orange marker:font-display marker:text-lg" {...props}>
      {children}
    </ol>
  ),
  li: ({ children, ...props }) => (
    <li
      className="font-body text-liftile-cream/80 text-base md:text-[17px] leading-[1.75] pl-6 relative before:content-[''] before:absolute before:left-0 before:top-[0.85em] before:w-3 before:h-px before:bg-liftile-orange"
      {...props}
    >
      {children}
    </li>
  ),
  blockquote: ({ children, ...props }) => (
    <blockquote
      className="my-8 pl-6 border-l-2 border-liftile-orange bg-liftile-orange/[0.04] py-4 pr-5 rounded-r-lg [&_p]:text-liftile-cream/90 [&_p]:italic [&_p]:my-0"
      {...props}
    >
      {children}
    </blockquote>
  ),
  table: ({ children, ...props }) => (
    <div className="my-8 overflow-x-auto rounded-xl border border-white/10">
      <table className="min-w-full text-left" {...props}>
        {children}
      </table>
    </div>
  ),
  thead: ({ children, ...props }) => (
    <thead className="bg-liftile-orange/15 border-b border-liftile-orange/30" {...props}>
      {children}
    </thead>
  ),
  th: ({ children, ...props }) => (
    <th
      className="font-heading text-[11px] uppercase tracking-[0.18em] text-liftile-orange font-semibold px-5 py-3.5"
      {...props}
    >
      {children}
    </th>
  ),
  tr: ({ children, ...props }) => (
    <tr className="border-b border-white/5 last:border-0" {...props}>
      {children}
    </tr>
  ),
  td: ({ children, ...props }) => (
    <td className="font-body text-sm md:text-[15px] text-liftile-cream/80 px-5 py-3 align-top" {...props}>
      {children}
    </td>
  ),
  hr: () => (
    <hr className="my-10 border-0 mx-auto w-16 h-px bg-liftile-orange/60" />
  ),
};

/* ===================================================================== */
export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [allPosts, setAllPosts] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    setPost(null);
    setError(false);
    axios
      .get(`${API}/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setError(true));
  }, [slug]);

  useEffect(() => {
    axios.get(`${API}/blog`).then((r) => setAllPosts(r.data)).catch(() => {});
  }, []);

  const toc = useMemo(() => extractToc(post?.content), [post]);
  const related = useMemo(() => {
    if (!post) return [];
    const sameCat = allPosts.filter(
      (p) => p.slug !== post.slug && p.category === post.category,
    );
    const others = allPosts.filter(
      (p) => p.slug !== post.slug && p.category !== post.category,
    );
    return [...sameCat, ...others].slice(0, 3);
  }, [post, allPosts]);

  if (error) {
    return (
      <>
        <Navbar />
        <div className="pt-32 min-h-[60vh] container-x text-center">
          <h1 className="display-2 text-liftile-cream">Article Not Found</h1>
          <Link to="/insights" className="btn-primary mt-6 inline-flex">
            Back To Construction Insights
          </Link>
        </div>
        <Footer />
      </>
    );
  }

  if (!post) {
    return (
      <>
        <Navbar />
        <div className="pt-32 min-h-[60vh] grid place-items-center text-liftile-cream/50 font-heading uppercase tracking-wider">
          Loading...
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Seo
        title={post.title}
        description={post.excerpt}
        path={`/insights/${post.slug}`}
        image={post.cover_image}
        type="article"
        jsonLd={[
          buildBreadcrumb([
            { name: "Home", path: "/" },
            { name: "Insights", path: "/insights" },
            { name: post.title, path: `/insights/${post.slug}` },
          ]),
          buildArticleSchema({
            title: post.title,
            description: post.excerpt,
            image: post.cover_image,
            path: `/insights/${post.slug}`,
            author: post.author,
            publishedAt: post.published_at,
          }),
        ]}
        jsonLdId={`article-${post.slug}`}
      />
      <Navbar />

      {/* Breadcrumb */}
      <div className="pt-24 bg-liftile-black border-b border-white/5">
        <div className="container-x py-5 flex items-center gap-2 text-[11px] text-liftile-cream/50 font-heading uppercase tracking-[0.18em]">
          <Link to="/" className="hover:text-liftile-orange">
            Home
          </Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/insights" className="hover:text-liftile-orange">
            Construction Insights
          </Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-liftile-orange truncate max-w-[360px]">
            {post.title}
          </span>
        </div>
      </div>

      {/* Article header */}
      <article className="bg-liftile-black pt-12 md:pt-16 pb-16 md:pb-20" data-testid="insights-article">
        <div className="container-x max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="heading-eyebrow mb-4">{post.category}</div>
            <h1
              data-testid="insights-article-title"
              className="font-display uppercase text-liftile-cream leading-[1.05] tracking-tight text-3xl sm:text-4xl md:text-5xl lg:text-[3.25rem]"
            >
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-7 text-[11px] uppercase tracking-[0.2em] font-heading text-liftile-cream/55">
              <span className="flex items-center gap-2">
                <User className="w-3.5 h-3.5 text-liftile-orange" /> {post.author}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-liftile-orange" /> {post.read_time}
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="w-3.5 h-3.5 text-liftile-orange" />{" "}
                {formatDate(post.published_at)}
              </span>
            </div>
          </motion.div>

          {/* Featured cover */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-10 aspect-[16/9] overflow-hidden rounded-2xl shadow-[0_20px_60px_-20px_rgba(255,107,26,0.25)]"
          >
            <PicImg
              src={post.cover_image}
              alt={post.title}
              className="w-full h-full object-cover" loading="lazy" decoding="async" />
          </motion.div>

          {/* Table of Contents */}
          {toc.length > 0 && (
            <motion.aside
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55 }}
              data-testid="insights-article-toc"
              className="mt-12 bg-white/[0.03] border border-white/10 rounded-2xl p-7 md:p-8"
            >
              <div className="flex items-center gap-3 mb-5">
                <ListTree className="w-4 h-4 text-liftile-orange" />
                <span className="font-heading text-[11px] uppercase tracking-[0.25em] text-liftile-orange font-semibold">
                  Table of Contents
                </span>
              </div>
              <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-3 list-none">
                {toc.map((item, i) => (
                  <li key={item.id} className="flex items-start gap-3">
                    <span className="font-display text-sm text-liftile-orange/70 mt-1 w-6 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <a
                      href={`#${item.id}`}
                      className="font-body text-sm md:text-[15px] text-liftile-cream/80 hover:text-liftile-orange transition-colors duration-300 leading-snug"
                    >
                      {item.title}
                    </a>
                  </li>
                ))}
              </ol>
            </motion.aside>
          )}

          {/* Article body — markdown */}
          <div
            data-testid="insights-article-content"
            className="mt-12 md:mt-14 prose-insights"
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
              {stripInlineToc(post.content)}
            </ReactMarkdown>
          </div>

          {/* Article Footer CTA */}
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            data-testid="insights-article-footer-cta"
            className="mt-16 md:mt-20 relative overflow-hidden rounded-2xl border border-liftile-orange/30 bg-gradient-to-br from-liftile-orange/[0.08] via-white/[0.02] to-transparent p-8 md:p-12 text-center"
          >
            <div
              aria-hidden
              className="absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-25 pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle, rgba(255,107,26,0.55) 0%, rgba(255,107,26,0) 70%)",
                filter: "blur(40px)",
              }}
            />
            <div className="mx-auto w-12 h-px bg-liftile-orange mb-6" />
            <h3 className="font-display uppercase text-liftile-cream tracking-tight text-2xl sm:text-3xl md:text-[2rem] leading-tight">
              Need Help Choosing the Right{" "}
              <span className="text-liftile-orange">Construction Equipment?</span>
            </h3>
            <p className="font-body text-liftile-cream/70 mt-5 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
              Our engineering team is ready to help you select the right
              solution for your project.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/contact"
                data-testid="article-cta-quote"
                className="group inline-flex items-center justify-center gap-3 bg-liftile-orange text-white font-heading font-semibold uppercase tracking-wider text-xs md:text-sm px-8 py-4 rounded-xl hover:-translate-y-0.5 hover:shadow-[0_18px_50px_-12px_rgba(255,107,26,0.85)] transition-all duration-400"
              >
                Request a Quote
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                data-testid="article-cta-contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-liftile-cream/30 text-liftile-cream font-heading text-xs md:text-sm font-semibold uppercase tracking-wider rounded-xl hover:bg-liftile-cream hover:text-liftile-matte-black transition-all duration-300"
              >
                Contact LIFTILE
              </Link>
            </div>
          </motion.div>

          {/* Back link */}
          <div className="mt-14 pt-7 border-t border-white/10">
            <Link
              to="/insights"
              data-testid="insights-back-link"
              className="font-heading text-xs uppercase tracking-[0.2em] text-liftile-orange inline-flex items-center gap-2 hover:gap-3 transition-all"
            >
              <ArrowLeft className="w-4 h-4" /> Back To Construction Insights
            </Link>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      {related.length > 0 && (
        <section
          data-testid="insights-related"
          className="bg-liftile-cream-light py-20 md:py-24 text-liftile-matte-black"
        >
          <div className="container-x">
            <div className="mb-10 md:mb-12">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-10 h-px bg-liftile-orange" />
                <span className="font-heading text-[10px] uppercase tracking-[0.3em] text-liftile-orange font-semibold">
                  Related Articles
                </span>
              </div>
              <h2 className="font-display uppercase text-liftile-matte-black tracking-tight text-3xl sm:text-4xl md:text-[2.5rem] leading-tight">
                Keep <span className="text-liftile-orange">Reading</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7 md:gap-8">
              {related.map((p, i) => (
                <ArticleCard key={p.id} post={p} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </>
  );
}
